import { ApolloCache, ServerError } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import * as Sentry from '@sentry/browser';
import { Auth } from '@aws-amplify/auth';
import { isEffectivelyOnline } from '@bilderit/offline-link';
import promiseToObservable from '@/utils/promiseToObservable';
import { isMobileApp } from '@/utils/isMobileApp';
import { resetPostHogIdentity } from '@/utils/analytics/posthog';
import { toast } from 'sonner';
import { isAuthorizationGraphQLError } from './isAuthorizationError';

// Guards against a burst of parallel 401s (several in-flight queries all
// rejected by the same expired/invalid token) triggering more than one
// sign-out.
let isSigningOut = false;

let apolloCache: ApolloCache<unknown> | undefined;

// errorLink needs the cache to evict denied fields (see below), but Apollo
// 3.8 doesn't expose the cache in the link context, and this module can't
// import it from graphql.tsx — graphql.tsx imports links.ts, so that would
// be a cycle. graphql.tsx hands the cache over once it's built instead.
/** Called once from graphql.tsx after the cache is constructed. */
export const registerApolloCache = (cache: ApolloCache<unknown>) => {
  apolloCache = cache;
};

export const errorLink = onError((params) => {
  const { graphQLErrors } = params;
  const networkError = params.networkError as ServerError;
  console.log(params);
  if (
    networkError?.message === 'Failed to fetch' ||
    networkError?.message === 'Load failed'
  ) {
    if (isMobileApp() && !isEffectivelyOnline('3g')) return;
    toast.error(
      'Hubo un error al comunicarse con nuestros servidores. Por favor espera unos segundos y vuelve a intentarlo, o refresca la página.',
      {
        duration: 3000,
      }
    );
    return;
  }
  if (
    networkError?.statusCode !== 403 &&
    networkError?.statusCode !== 401 &&
    networkError?.statusCode !== 502
  ) {
    const { ...info } = params;
    const nonExpectedGQLErrors = graphQLErrors?.filter(
      (error) =>
        error.extensions?.code !== 'BILDERIT_EXPECTED_ERROR' &&
        !error.extensions.isBilderitExpectedError
    );
    if (networkError)
      Sentry.captureException(networkError, {
        tags: { networkError: 'true' },
        extra: { ...info },
      });
    if (nonExpectedGQLErrors?.length)
      nonExpectedGQLErrors.forEach((error) =>
        Sentry.captureMessage(error.message, {
          level: 'error',
          tags: { graphQLError: 'true' },
          extra: { ...info },
        })
      );
  }
  const mainDefinition = getMainDefinition(params.operation.query);
  const isQuery =
    mainDefinition.kind === 'OperationDefinition' &&
    mainDefinition.operation === 'query';
  let evicted = false;
  for (const graphQLError of graphQLErrors ?? []) {
    if (isAuthorizationGraphQLError(graphQLError)) {
      toast.error(
        `Error: no estas autorizado a realizar esta operación. ${
          params.operation.operationName
        }(${JSON.stringify(params.operation.variables)})`,
        {
          duration: 3000,
        }
      );
      // A NOT_AUTHORIZED/FORBIDDEN response evicts exactly the denied root
      // field (all argument variants, since we evict by fieldName alone),
      // so the next cache read can't serve data the server just denied.
      // Queries only — a denied mutation has no cached field to evict.
      // Deliberately narrow: never resetStore/clearStore here, since on the
      // offline-first PWA that would throw away an operator's whole offline
      // dataset over one denied query. The screen-side half of this lives
      // in apolloHooks.ts (suppressDeniedData); this eviction is also what
      // covers the errorPolicy:'all' data:null edge — see the regression
      // test in apolloHooks.test.tsx.
      const fieldName = graphQLError.path?.[0];
      if (isQuery && apolloCache && typeof fieldName === 'string') {
        apolloCache.evict({ id: 'ROOT_QUERY', fieldName });
        evicted = true;
      }
    }
  }
  if (evicted) {
    apolloCache?.gc();
  }
  if (networkError?.statusCode === 401) {
    // Session is over (expired/invalid token) — sign out. No eviction here.
    if (isEffectivelyOnline('3g') && !isSigningOut) {
      isSigningOut = true;
      toast.error('Tu sesión expiró. Por favor, vuelve a iniciar sesión.', {
        duration: 3000,
      });
      // This sign-out bypasses the auth machine (AmplifyMachineConfig), so
      // the analytics identity must be reset here too — same order as
      // there: flush the user's queued events, reset, then sign out.
      // resetPostHogIdentity never rejects.
      resetPostHogIdentity()
        .then(() => Auth.signOut())
        .finally(() => {
          window.location.replace('/login');
        });
    }
  }
  if (networkError?.statusCode === 403) {
    // No eviction here either — see below, clock drift isn't a real denial.
    console.warn(
      'Error 403. Probably your clock has drifted. Refreshing credentials.'
    );
    // update credentials to get clock drift
    return promiseToObservable(
      Auth.currentAuthenticatedUser({
        bypassCache: true, // this will get the latest clockDrift
      })
    ).flatMap(() => params.forward(params.operation));
  }
});
